import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@/pages/strategic/hooks/useAutoSaveParams';

/**
 * Custom hook for Publishing Review Updates auto-save functionality
 * 
 * Manages auto-save detection and restoration for Publishing Review Updates with:
 * - Tracks reviewsData changes during edit mode
 * - Project-specific auto-save by including project ID
 * 
 */
export const useReviewUpdatesAutoSave = (
    isEditing: boolean,
    reviewsData: any[],
    projectId: string,
    setReviewsData: (data: any) => void
) => {
    // Auto-save state
    const [autoSaveVisible, setAutoSaveVisible] = useState(false);
    const [editData, setEditData] = useState<any[]>([]);

    // Auto-save: construct unique pageKey by appending project ID
    // Each project has separate auto-save storage
    const reviewUpdatesPageKey = useMemo(() => {
        if (!projectId) return '';
        return `${EDIT_PAGE_KEY_MAP.publishing_project_review_updates}_${projectId}`;
    }, [projectId]);

    // Auto-save: get methods (check if auto-save exists)
    const autoSaveMethods: any = useAutoSaveParams({
        pageKey: reviewUpdatesPageKey,
    });

    const {
        getSaveParamsValue,
        showRestorePrompt,
        deleteSaveParamsByPageKey
    } = autoSaveMethods;

    // Track edit data changes - update editData whenever reviewsData changes in edit mode
    useEffect(() => {
        if (isEditing) {
            setEditData(reviewsData);
        } else {
            // Reset when exiting edit mode
            setEditData([]);
        }
    }, [isEditing, reviewsData]);

    // Current save params for auto-save comparison
    const currentSaveParams = useMemo(() => {
        if (!isEditing) {
            // When not editing, return empty params to prevent auto-save from triggering
            return {
                reviewsData: [],
                projectId: '',
            };
        }

        // When editing, use actual edit data
        return {
            reviewsData: editData.length > 0 ? editData : reviewsData,
            projectId: projectId,
        };
    }, [isEditing, editData, reviewsData, projectId]);

    // Memoize initialParams to capture the starting state when entering edit mode
    const initialParamsRef = useRef<{
        reviewsData: any[];
        projectId: string;
    }>({
        reviewsData: [],
        projectId: '',
    });

    // Update initialParams when entering edit mode (capture the original state)
    useEffect(() => {
        if (isEditing && reviewsData.length > 0) {
            // Only update if initialParams is empty (first time entering edit mode)
            if (initialParamsRef.current.reviewsData.length === 0) {
                initialParamsRef.current = {
                    reviewsData: JSON.parse(JSON.stringify(reviewsData)), // Deep clone to avoid reference issues
                    projectId: projectId,
                };
                console.log('🎯 [REVIEW UPDATES AUTO-SAVE] Captured initial data:', initialParamsRef.current);
            }
        } else if (!isEditing) {
            // Reset initialParams when exiting edit mode
            initialParamsRef.current = {
                reviewsData: [],
                projectId: '',
            };
        }
    }, [isEditing, reviewsData, projectId]);

    useAutoSaveParams({
        pageKey: reviewUpdatesPageKey,
        initialParams: initialParamsRef.current,
        saveParamsCur: currentSaveParams,
        isBegin: isEditing,
        isChangeValue: true,
    });

    // Handler: Check for auto-save before entering edit mode
    const handleReviewUpdatesEdit = useCallback((originalEditHandler: () => void) => {
        if (!reviewUpdatesPageKey) {
            originalEditHandler();
            return;
        }

        if (showRestorePrompt(reviewUpdatesPageKey)) {
            setAutoSaveVisible(true);
        } else {
            originalEditHandler();
        }
    }, [showRestorePrompt, reviewUpdatesPageKey]);

    // Handler: Discard auto-save and enter edit mode
    const handleAutoSaveCancel = useCallback((originalEditHandler: () => void) => {
        setAutoSaveVisible(false);
        if (reviewUpdatesPageKey) {
            deleteSaveParamsByPageKey(reviewUpdatesPageKey);
        }
        originalEditHandler();
    }, [deleteSaveParamsByPageKey, reviewUpdatesPageKey]);

    // Handler: Restore from auto-save
    const handleAutoSaveConfirm = useCallback((originalEditHandler: () => void) => {
        const saveValue = getSaveParamsValue(reviewUpdatesPageKey) || {};

        console.log('🔄 [REVIEW UPDATES AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

        setAutoSaveVisible(false);

        // Check if projectId changed (user switched projects)
        if (saveValue.projectId && saveValue.projectId !== projectId) {
            // Clear auto-save data for the wrong project
            if (reviewUpdatesPageKey) {
                deleteSaveParamsByPageKey(reviewUpdatesPageKey);
            }
            originalEditHandler();
            return;
        }

        // Update reviewsData with data from auto-save
        if (saveValue.reviewsData && Array.isArray(saveValue.reviewsData) && saveValue.reviewsData.length > 0) {
            setReviewsData(saveValue.reviewsData);
        }

        // Enter edit mode
        originalEditHandler();
    }, [getSaveParamsValue, reviewUpdatesPageKey, projectId, deleteSaveParamsByPageKey, setReviewsData]);

    // Clear auto-save after successful save
    const clearReviewUpdatesAutoSave = useCallback(() => {
        if (reviewUpdatesPageKey) {
            deleteSaveParamsByPageKey(reviewUpdatesPageKey);
        }
    }, [deleteSaveParamsByPageKey, reviewUpdatesPageKey]);

    // Check if there are unsaved changes for RoutePrompt
    const hasReviewUnsavedChanges = useMemo(() => {
        if (!isEditing) return false;
        
        const initialData = initialParamsRef.current?.reviewsData;
        if (!initialData || initialData.length === 0) return false;
        
        const currentData = editData.length > 0 ? editData : reviewsData;
        
        // Compare using JSON stringify
        const hasChanges = JSON.stringify(initialData) !== JSON.stringify(currentData);
        
        return hasChanges;
    }, [isEditing, editData, reviewsData]);

    return {
        // State
        autoSaveVisible,
        reviewUpdatesPageKey,
        hasReviewUnsavedChanges,

        // Handlers
        handleReviewUpdatesEdit,
        handleAutoSaveCancel,
        handleAutoSaveConfirm,
        clearReviewUpdatesAutoSave,
    };
};
